import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import CheckboxField from '../../../../components/forms/CheckboxField';
import ComboboxSelect from '../../../../components/forms/ComboboxSelect';
import ErrorMessage from '../../../../components/forms/ErrorMessage';
import SubmitButton from '../../../../components/forms/SubmitButton';
import TextInput from '../../../../components/forms/TextInput';

import { useAuth } from '../../../../hooks/useAuth';
import { useForm } from '../../../../hooks/useForm';
import { useScrollToTop } from '../../../../hooks/useScrollToTop';

import BrregOrganizationField from './BrregOrganizationField';
import { NOK_RELATIONS } from './register.constants';

function RegisterPage() {
    const navigate = useNavigate();
    const scrollToTop = useScrollToTop();
    const { user, refresh } = useAuth();

    const [apiError, setApiError] = useState(null);

    const form = useForm({
        defaultValues: {
            firstname: '',
            surname: '',
            email: '',
            organizationName: '',
            organizationSelectedKey: '',
            organizationUseFreeText: false,
            organizationFreeText: '',
            nokName: '',
            nokPhone: '',
            nokRelation: '',
            fagConsent: false,
            nextSarConsent: true
        }
    });

    const onSubmit = async data => {
        setApiError(null);

        try {
            const res = await fetch('/api/auth/registrer_bruker.php', {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    firstname: data.firstname?.trim() || '',
                    surname: data.surname?.trim() || '',
                    email: data.email?.trim() || '',
                    org:
                        (data.organizationUseFreeText
                            ? data.organizationFreeText
                            : data.organizationName
                        )?.trim() || '',
                    nokName: data.nokName?.trim() || '',
                    nokPhone: data.nokPhone?.trim() || '',
                    nokRelation: data.nokRelation?.trim() || '',
                    fagConsent: data.fagConsent ? 'J' : 'N',
                    nextSarConsent: data.nextSarConsent ? 'J' : 'N'
                })
            });

            const json = await res.json();
            if (!json.success) throw new Error(json.message || 'Kunne ikke registrere bruker.');

            // Make sure auth state updates (hasProfile etc.)
            await refresh();

            // Make AuthGate route correctly
            navigate('/', { replace: true });
            scrollToTop('smooth');
        } catch (err) {
            setApiError(err.message);
        }
    };

    const nokRelationValue = form.watch('nokRelation');
    const nokRelationReg = form.register('nokRelation', {
        required: 'Pårørendes relasjon er påkrevd.',
        validate: v => !!(v || '').trim() || 'Pårørendes relasjon er påkrevd.'
    });

    return (
        <div className="minside-page minside-auth">
            <div className="container container--narrow">
                <header className="minside-header">
                    <h1 className="minside-title">Min side</h1>
                    <p className="minside-subtitle">
                        Fullfør registrering for å melde deg på arrangementet og få tilgang til Min
                        side.
                    </p>
                </header>

                <div className="minside-card">
                    <form onSubmit={form.handleSubmit(onSubmit)} className="minside-form">
                        <h4>Registrering</h4>
                        <p className="minside-note">
                            Fyll inn opplysningene dine under for å melde deg på SAR61˚N og få
                            tilgang til Min side der du kan administrere ditt opphold.
                        </p>
                        {user?.mobile && (
                            <p className="minside-note">
                                Du er logget inn med telefonnummer: <strong>{user.mobile}</strong>
                            </p>
                        )}

                        <div className="divider"></div>
                        <h5>Deltakers opplysninger</h5>
                        <TextInput
                            autoFocus
                            label="Fornavn*"
                            name="firstname"
                            type="text"
                            autocomplete="given-name"
                            error={form.formState.errors.firstname}
                            disableTab={form.formState.isSubmitSuccessful}
                            {...form.register('firstname', {
                                required: 'Fornavn er påkrevd.',
                                minLength: { value: 1, message: 'Fornavnet er for kort.' }
                            })}
                        />

                        <TextInput
                            label="Etternavn*"
                            name="surname"
                            type="text"
                            autocomplete="family-name"
                            error={form.formState.errors.surname}
                            disableTab={form.formState.isSubmitSuccessful}
                            {...form.register('surname', {
                                required: 'Etternavn er påkrevd.',
                                minLength: { value: 1, message: 'Etternavnet er for kort.' }
                            })}
                        />

                        <TextInput
                            label="E-post*"
                            name="email"
                            type="email"
                            autocomplete="email"
                            error={form.formState.errors.email}
                            disableTab={form.formState.isSubmitSuccessful}
                            {...form.register('email', {
                                required: 'E-post er påkrevd.',
                                pattern: {
                                    value: /\S+@\S+\.\S+/,
                                    message: 'Ugyldig e-postadresse.'
                                },
                                maxLength: {
                                    value: 50,
                                    message: 'E-posten er for lang.'
                                }
                            })}
                        />

                        <BrregOrganizationField
                            form={form}
                            disableTab={form.formState.isSubmitSuccessful}
                        />

                        <div className="divider"></div>
                        <h5>Pårørendes opplysninger</h5>
                        <TextInput
                            label="Pårørende - Navn*"
                            name="nokName"
                            type="text"
                            autocomplete="off"
                            error={form.formState.errors.nokName}
                            disableTab={form.formState.isSubmitSuccessful}
                            {...form.register('nokName', {
                                required: 'Pårørendes navn er påkrevd.',
                                maxLength: {
                                    value: 50,
                                    message: 'Pårørendes navn er for langt.'
                                }
                            })}
                        />

                        <TextInput
                            label="Pårørende - Telefon*"
                            name="nokPhone"
                            type="tel"
                            autocomplete="off"
                            error={form.formState.errors.nokPhone}
                            disableTab={form.formState.isSubmitSuccessful}
                            {...form.register('nokPhone', {
                                required: 'Pårørendes telefonnummer er påkrevd.',
                                maxLength: {
                                    value: 12,
                                    message: 'Pårørendes telefonnummer er for langt.'
                                }
                            })}
                        />

                        <ComboboxSelect
                            label="Pårørende - Relasjon*"
                            name={nokRelationReg.name}
                            ref={nokRelationReg.ref}
                            value={nokRelationValue}
                            onChange={nokRelationReg.onChange}
                            onBlur={nokRelationReg.onBlur}
                            options={NOK_RELATIONS}
                            error={form.formState.errors.nokRelation}
                            disableTab={form.formState.isSubmitSuccessful}
                        />

                        <div className="divider"></div>

                        <CheckboxField
                            name="nextSarConsent"
                            label="Jeg ønsker å motta informasjon om neste SAR61˚N i 2028."
                            disableTab={form.formState.isSubmitSuccessful}
                            {...form.register('nextSarConsent')}
                        />

                        <CheckboxField
                            name="fagConsent"
                            label="Jeg ønsker å motta faglig informasjon og relevante oppdateringer fra Stiftelsen Norsk Luftambulanse på e-post."
                            disableTab={form.formState.isSubmitSuccessful}
                            {...form.register('fagConsent')}
                        />

                        <p className="consent-text">
                            Ved å fullføre påmeldingen oppretter du en profil og samtykker til at vi
                            behandler personopplysningene dine for å administrere din deltakelse på
                            SAR61˚N. Les mer i vår <a href="/personvern/">personvernerklæring</a>.
                        </p>

                        <div className="form-submit-row form-submit-row--auth">
                            <div className="form-submit-row__message">
                                {apiError && <ErrorMessage message={apiError} />}
                            </div>

                            <div className="form-submit-row__actions">
                                <SubmitButton
                                    isLoading={form.formState.isSubmitting}
                                    disableTab={form.formState.isSubmitSuccessful}
                                >
                                    Fullfør registrering
                                </SubmitButton>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default RegisterPage;
